/**
 * apps/web/src/lib/dates.ts — the date/period display contract.
 *
 * Spec: 14-theme.md §8.1. The wire format is ISO-8601 `YYYY-MM-DD` and is
 * NEVER rendered; business dates are interpreted in Asia/Kuala_Lumpur and the
 * server never applies an offset, so a plain `dayjs(isoDate)` parse is correct.
 */
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

/** `28 Aug 2026` — a date in a table or a read-only field. */
export const DATE_FORMAT = 'DD MMM YYYY';
/** `Fri 28 Aug 2026` — the day-sheet header. */
export const DATE_WEEKDAY_FORMAT = 'ddd DD MMM YYYY';
/** `28 Aug 2026 09:14` — an event timestamp, converted from UTC for display. */
export const DATETIME_FORMAT = 'DD MMM YYYY HH:mm';

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dayjs(iso).format(DATE_FORMAT);
}

export function formatDateWithWeekday(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dayjs(iso).format(DATE_WEEKDAY_FORMAT);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dayjs(iso).format(DATETIME_FORMAT);
}

/** `AUG 2026` — the top-bar period chip. Uppercased, mono. */
export function formatPeriodLabel(monthKey: string | null | undefined): string {
  if (!monthKey) return '—';
  return dayjs(`${monthKey}-01`).format('MMM YYYY').toUpperCase();
}

/** `Aug 2026` — the payslip meta line. */
export function formatPeriodShort(monthKey: string | null | undefined): string {
  if (!monthKey) return '—';
  return dayjs(`${monthKey}-01`).format('MMM YYYY');
}

/** The wire form. Always `YYYY-MM-DD`, never localised. */
export function toIsoDate(d: Dayjs): string {
  return d.format('YYYY-MM-DD');
}

/** Today, in wire form. */
export function todayIso(): string {
  return dayjs().format('YYYY-MM-DD');
}

/**
 * `in 11 days` / `28 days ago` / `today` — the document-expiry delta text
 * (13-ui-screens.md §10.3). Whole days, computed on the calendar date.
 */
export function relativeDays(iso: string | null | undefined, from?: string): string {
  if (!iso) return '—';
  const base = dayjs(from ?? todayIso()).startOf('day');
  const target = dayjs(iso).startOf('day');
  const diff = target.diff(base, 'day');
  if (diff === 0) return 'today';
  if (diff > 0) return `in ${diff} day${diff === 1 ? '' : 's'}`;
  const ago = Math.abs(diff);
  return `${ago} day${ago === 1 ? '' : 's'} ago`;
}
